import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SiteHeader } from '../components/SiteHeader';
import { SEO } from '../components/SEO';

/**
 * Confirmation Statement (CS01) request flow.
 *
 * Structured form — company number, company name and director details
 * sit as proper fields at the top, not hidden in a free-text message.
 * The customer then declares whether anything has changed since the
 * last CS; if yes we ask them to describe the change so we can carry it
 * through into the filing.
 *
 * Submits as a service enquiry today; once Stripe checkout for CS01 is
 * wired we'll swap the button to a paid checkout redirect.
 */
export function ConfirmationStatement() {
  const [form, setForm] = useState({
    companyNumber: '',
    companyName: '',
    directorName: '',
    email: '',
    phone: '',
    hasChanges: '' as '' | 'no' | 'yes',
    changeDetails: '',
    website: '', // honeypot — must stay empty
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.hasChanges) {
      setError('Please tell us whether any company details have changed.');
      return;
    }
    if (form.hasChanges === 'yes' && form.changeDetails.trim().length < 10) {
      setError('Please describe the changes (at least 10 characters).');
      return;
    }
    setStatus('sending');
    setError(null);
    try {
      const res = await fetch('/api/orders/cs01', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyNumber: form.companyNumber.trim(),
          companyName: form.companyName.trim(),
          directorName: form.directorName.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          hasChanges: form.hasChanges,
          changeDetails: form.hasChanges === 'yes' ? form.changeDetails.trim() : undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || `Failed (${res.status})`);
      if (data.checkoutUrl) {
        // Hand off to Stripe — payment success returns to /success/:orderId.
        window.location.href = data.checkoutUrl;
        return;
      }
      throw new Error('Payment session could not be started.');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Could not start payment. Try again.');
    }
  }

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SEO
        title="Confirmation Statement (CS01) filing — Dormant Accounts Filing"
        description="File your annual Confirmation Statement (CS01) with Companies House. Simple structured form, filed by us within one working day."
        path="/confirmation-statement"
      />
      <SiteHeader />

      <main className="mx-auto max-w-3xl px-6 py-12">
        <Link to="/services" className="text-sm text-brand-700 hover:underline">
          ← Back to services
        </Link>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
          File a Confirmation Statement
        </h1>
        <p className="mt-3 text-slate-600">
          We file the CS01 for you direct to Companies House. Fill in your company details below;
          if anything's changed since your last CS we'll capture those changes too.
        </p>

        {status === 'sent' ? (
          <div className="mt-8 rounded-xl bg-green-50 p-8 ring-1 ring-green-200">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-2xl text-green-700">
              ✓
            </div>
            <h2 className="mt-4 text-xl font-semibold text-green-900">Request received</h2>
            <p className="mt-2 text-sm text-green-800">
              Thanks. We'll confirm the price and next steps by email, usually within a few hours.
            </p>
            <Link to="/services" className="btn-ghost mt-6 inline-flex">Back to services</Link>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="mt-8 space-y-6 rounded-xl bg-white p-6 ring-1 ring-slate-200 sm:p-8"
          >
            {/* Company block */}
            <section>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Company
              </h2>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <Field label="Company number" required>
                  <input
                    type="text"
                    value={form.companyNumber}
                    onChange={set('companyNumber')}
                    required
                    pattern="[A-Za-z0-9]{6,10}"
                    className="input font-mono"
                    placeholder="12345678"
                  />
                </Field>
                <Field label="Company name" required>
                  <input
                    type="text"
                    value={form.companyName}
                    onChange={set('companyName')}
                    required
                    autoComplete="organization"
                    className="input"
                    placeholder="Acme Holdings Ltd"
                  />
                </Field>
              </div>
            </section>

            {/* Contact block */}
            <section>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Director / contact
              </h2>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <Field label="Director name" required>
                  <input
                    type="text"
                    value={form.directorName}
                    onChange={set('directorName')}
                    required
                    autoComplete="name"
                    className="input"
                    placeholder="Jane Smith"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    type="email"
                    value={form.email}
                    onChange={set('email')}
                    required
                    autoComplete="email"
                    className="input"
                    placeholder="jane@example.com"
                  />
                </Field>
                <Field label="Phone (optional)" className="sm:col-span-2">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={set('phone')}
                    autoComplete="tel"
                    className="input"
                    placeholder="07123 456 789"
                  />
                </Field>
              </div>
            </section>

            {/* Changes block */}
            <section>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                Changes since your last Confirmation Statement?
              </h2>
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                <RadioCard
                  selected={form.hasChanges === 'no'}
                  onClick={() => setForm((f) => ({ ...f, hasChanges: 'no', changeDetails: '' }))}
                  title="No changes"
                  sub="Re-confirm the existing record"
                />
                <RadioCard
                  selected={form.hasChanges === 'yes'}
                  onClick={() => setForm((f) => ({ ...f, hasChanges: 'yes' }))}
                  title="Yes, there are changes"
                  sub="Tell us what's changed"
                />
              </div>

              {form.hasChanges === 'yes' && (
                <Field label="What's changed?" required className="mt-4">
                  <textarea
                    value={form.changeDetails}
                    onChange={set('changeDetails')}
                    required
                    minLength={10}
                    rows={5}
                    className="input"
                    placeholder="e.g. registered office address has changed to [...], new shareholder added, SIC code updated, etc."
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Include all that apply: registered office address, shareholders, SIC code,
                    persons with significant control (PSCs), etc.
                  </p>
                </Field>
              )}
            </section>

            {/* Honeypot — bots fill every visible field, this one's off-screen. */}
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={set('website')}
              autoComplete="off"
              tabIndex={-1}
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}
            />

            {error && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
            )}

            <div className="flex flex-col items-center gap-3 border-t border-slate-100 pt-6">
              <button
                type="submit"
                disabled={status === 'sending'}
                className="btn-primary w-full justify-center sm:w-auto sm:min-w-[260px]"
              >
                {status === 'sending' ? 'Starting payment…' : 'Pay £90 & continue →'}
              </button>
              <p className="text-[11px] text-slate-500">
                Fixed price £90 – CS01 filed via Companies House within 1 working day of payment.
              </p>
            </div>
          </form>
        )}
      </main>
    </div>
  );
}

function Field({
  label,
  required,
  children,
  className = '',
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="text-amber-600"> *</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function RadioCard({
  selected,
  onClick,
  title,
  sub,
}: {
  selected: boolean;
  onClick: () => void;
  title: string;
  sub: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-lg border p-4 text-left transition ${
        selected
          ? 'border-brand-500 bg-brand-50 ring-2 ring-brand-500'
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <div className="flex items-center gap-2">
        <span
          className={`flex h-4 w-4 items-center justify-center rounded-full border ${
            selected ? 'border-brand-600 bg-brand-600' : 'border-slate-300'
          }`}
        >
          {selected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
        </span>
        <span className="font-medium text-slate-900">{title}</span>
      </div>
      <p className="mt-1 text-xs text-slate-500">{sub}</p>
    </button>
  );
}
